import './App.css';
import { useState, useEffect } from 'react';

function ReviewDateGroup({ date, words }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="review-date-group">
      <button className="review-date-header" onClick={() => setOpen(o => !o)}>
        <span>📅 {date}</span>
        <span>{words.length} word{words.length !== 1 ? 's' : ''} {open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="review-grid">
          {words.map((word, i) => (
            <div key={i} className="review-card">
              <div className="review-word">{word.word}</div>
              <div className="review-meaning">{word.meaning}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function App() {
  // Authentication state
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authView, setAuthView] = useState('login'); // login, signup
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  
  // App state
  const [todaysWords, setTodaysWords] = useState([]);
  const [learnedWords, setLearnedWords] = useState([]);
  const [currentView, setCurrentView] = useState('home'); // home, learn, review, quiz, progress
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [totalWordsLearned, setTotalWordsLearned] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [quizState, setQuizState] = useState({
    currentQuestion: 0,
    score: 0,
    questions: [],
    showResult: false,
    selectedAnswer: null
  });

  // API endpoint from your Terraform output
  const API_URL = 'https://mxwn0n9gqf.execute-api.ap-southeast-1.amazonaws.com';

  useEffect(() => {
    // Check for existing session
    const savedUser = localStorage.getItem('wordsprout_user');
    if (savedUser) {
      const userData = JSON.parse(savedUser);
      setUser(userData);
      setIsAuthenticated(true);
      setTotalWordsLearned(userData.total_words_learned || 0);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchTodaysWords();
      loadUserProgress();
    }
  }, [isAuthenticated, user]);

  const fetchTodaysWords = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(API_URL);
      if (!response.ok) {
        throw new Error('Failed to fetch words');
      }
      const words = await response.json();
      setTodaysWords(words);
    } catch (err) {
      setError('Unable to load today\'s words. Please try again later.');
      console.error('Error fetching words:', err);
      // Fallback to sample words for demo
      setTodaysWords([
        {
          word: 'HAPPY',
          meaning: 'Feeling joy and pleasure',
          example: 'I am happy when I play with my friends.'
        },
        {
          word: 'BRAVE',
          meaning: 'Having courage and not being afraid',
          example: 'The brave firefighter saved the cat from the tree.'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadUserProgress = async () => {
    if (!user?.user_id) return;
    
    try {
      const response = await fetch(`${API_URL}/progress?user_id=${user.user_id}`);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setLearnedWords(data.learned_words || []);
          setTotalWordsLearned(data.total_words_learned || 0);
        }
      }
    } catch (err) {
      console.error('Error loading user progress:', err);
      // Fallback to localStorage for offline support
      const saved = localStorage.getItem('wordsprout_progress');
      if (saved) {
        const progress = JSON.parse(saved);
        setLearnedWords(progress.learnedWords || []);
        setTotalWordsLearned(progress.totalWordsLearned || 0);
      }
    }
  };

  const saveProgress = async (newLearnedWords, total) => {
    if (!user?.user_id) return;
    
    try {
      const response = await fetch(`${API_URL}/progress`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.user_id,
          learned_words: newLearnedWords,
          total_count: total
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          console.log('Progress saved to server');
        }
      }
    } catch (err) {
      console.error('Error saving progress to server:', err);
    }
    
    // Always save locally as backup
    const progress = {
      learnedWords: newLearnedWords,
      totalWordsLearned: total,
      lastUpdated: new Date().toISOString()
    };
    localStorage.setItem('wordsprout_progress', JSON.stringify(progress));
  };

  const markWordAsLearned = (word) => {
    // Check if word is already learned to prevent duplicate counting
    const isAlreadyLearned = learnedWords.some(learnedWord => learnedWord.word === word.word);
    
    if (isAlreadyLearned) {
      alert('You have already learned this word! 🌟');
      return;
    }

    const wordWithDate = { ...word, learnedDate: new Date().toISOString() };
    const newLearnedWords = [...learnedWords, wordWithDate];
    const newTotal = totalWordsLearned + 1;
    
    setLearnedWords(newLearnedWords);
    setTotalWordsLearned(newTotal);
    saveProgress(newLearnedWords, newTotal);
    
    alert('Great job! Word learned! 🎉');
  };

  const generateQuizQuestions = () => {
    if (todaysWords.length === 0) return [];
    
    return todaysWords.map((word, index) => {
      // Create wrong options from other words
      const wrongOptions = todaysWords
        .filter((_, i) => i !== index)
        .map(w => w.word)
        .slice(0, 3);
      
      // Add the correct word and shuffle
      const options = [...wrongOptions, word.word].sort(() => Math.random() - 0.5);
      
      return {
        sentence: word.example,
        correctAnswer: word.word,
        options: options,
        word: word
      };
    });
  };

  const startQuiz = () => {
    const questions = generateQuizQuestions();
    setQuizState({
      currentQuestion: 0,
      score: 0,
      questions: questions,
      showResult: false,
      selectedAnswer: null
    });
  };

  const handleQuizAnswer = (selectedOption) => {
    setQuizState(prev => ({ ...prev, selectedAnswer: selectedOption }));
  };

  const submitQuizAnswer = () => {
    const { currentQuestion, questions, score, selectedAnswer } = quizState;
    const isCorrect = selectedAnswer === questions[currentQuestion].correctAnswer;
    
    const newScore = isCorrect ? score + 1 : score;
    
    if (currentQuestion + 1 < questions.length) {
      setQuizState(prev => ({
        ...prev,
        currentQuestion: currentQuestion + 1,
        score: newScore,
        selectedAnswer: null
      }));
    } else {
      setQuizState(prev => ({
        ...prev,
        score: newScore,
        showResult: true
      }));
    }
  };

  // Authentication functions
  const handleLogin = async (username, password) => {
    setAuthLoading(true);
    setAuthError(null);
    
    try {
      const response = await fetch(`${API_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setUser(data);
        setIsAuthenticated(true);
        setTotalWordsLearned(data.total_words_learned || 0);
        localStorage.setItem('wordsprout_user', JSON.stringify(data));
      } else {
        setAuthError(data.message || 'Login failed');
      }
    } catch (err) {
      setAuthError('Network error. Please try again.');
      console.error('Login error:', err);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    setUser(null);
    setIsAuthenticated(false);
    setLearnedWords([]);
    setTotalWordsLearned(0);
    setCurrentView('home');
    setLoginUsername('');
    setLoginPassword('');
    setAuthError(null);
    localStorage.removeItem('wordsprout_user');
    localStorage.removeItem('wordsprout_progress');
  };

  const switchAuthView = (view) => {
    setAuthView(view);
    setAuthError(null);
    setLoginUsername('');
    setLoginPassword('');
  };

  const renderAuth = () => (
    <div className="auth-container">
      <div className="auth-header">
        <h1 className="app-title">🌱 WordSprout</h1>
        <p className="app-subtitle">Grow your vocabulary, one word at a time</p>
      </div>
      
      <div className="auth-form">
        <div className="auth-tabs">
          <button 
            onClick={() => switchAuthView('login')} 
            className={`auth-tab ${authView === 'login' ? 'active' : ''}`}
          >
            Login
          </button>
          <button 
            onClick={() => switchAuthView('signup')} 
            className={`auth-tab ${authView === 'signup' ? 'active' : ''}`}
          >
            Sign Up
          </button>
        </div>
        
        {authView === 'login' ? renderLoginForm() : renderSignupForm()}
      </div>
    </div>
  );

  const renderLoginForm = () => {
    const onSubmit = (e) => {
      e.preventDefault();
      if (loginUsername.trim() && loginPassword.trim()) {
        handleLogin(loginUsername.trim(), loginPassword);
      }
    };
    
    return (
      <form onSubmit={onSubmit} className="auth-form-content">
        <h2>Welcome Back!</h2>
        
        {authError && <div className="auth-error">{authError}</div>}
        
        <div className="form-group">
          <label>Username</label>
          <input
            type="text"
            value={loginUsername}
            onChange={(e) => setLoginUsername(e.target.value)}
            placeholder="Enter your username"
            required
          />
        </div>
        
        <div className="form-group">
          <label>Password</label>
          <input
            type="password"
            value={loginPassword}
            onChange={(e) => setLoginPassword(e.target.value)}
            placeholder="Enter your password"
            required
          />
        </div>
        
        <button type="submit" disabled={authLoading} className="auth-btn primary">
          {authLoading ? 'Logging in...' : 'Login'}
        </button>
      </form>
    );
  };

  const renderSignupForm = () => (
    <div className="auth-form-content">
      <h2>Create Account</h2>
      <div className="signup-info">
        <p>📝 To create a new account, please contact your administrator.</p>
        <p>💡 Accounts are created using the user management script.</p>
        <p>🔑 Once your account is created, you can login above.</p>
      </div>
      <button 
        onClick={() => switchAuthView('login')} 
        className="auth-btn secondary"
      >
        Back to Login
      </button>
    </div>
  );

  const renderHome = () => (
    <div className="home-container">
      <div className="header-section">
        <h1 className="app-title">🌱 WordSprout</h1>
        <p className="app-subtitle">Welcome back, {user?.name || user?.username}!</p>
        <button onClick={handleLogout} className="logout-btn">
          Logout
        </button>
      </div>
      
      <div className="stats-section">
        <div className="stat-card primary">
          <div className="stat-number">{totalWordsLearned}</div>
          <div className="stat-label">Words Learned</div>
        </div>
        <div className="stat-card secondary">
          <div className="stat-number">{todaysWords.length}</div>
          <div className="stat-label">Today's Words</div>
        </div>
      </div>

      <div className="action-buttons">
        <button onClick={() => setCurrentView('learn')} className="action-btn primary">
          <span className="btn-icon">📚</span>
          <span className="btn-text">Learn Today's Words</span>
        </button>
        <button onClick={() => setCurrentView('review')} className="action-btn secondary">
          <span className="btn-icon">🔄</span>
          <span className="btn-text">Review Words</span>
        </button>
        <button onClick={() => { startQuiz(); setCurrentView('quiz'); }} className="action-btn accent">
          <span className="btn-icon">🎯</span>
          <span className="btn-text">Fun Quiz</span>
        </button>
        <button onClick={() => setCurrentView('progress')} className="action-btn info">
          <span className="btn-icon">📊</span>
          <span className="btn-text">My Progress</span>
        </button>
      </div>
    </div>
  );

  const renderLearn = () => {
    if (loading) return <div className="loading-state">Loading today's words... 🌱</div>;
    if (error) return <div className="error-state">{error}</div>;
    if (todaysWords.length === 0) return <div className="empty-state">No words available today.</div>;

    const currentWord = todaysWords[currentWordIndex];
    const isWordLearned = learnedWords.some(learnedWord => learnedWord.word === currentWord.word);
    
    return (
      <div className="learn-container">
        <button onClick={() => setCurrentView('home')} className="home-btn">🏠 Home</button>
        <div className="progress-header">
          <div className="progress-bar">
            <div className="progress-fill" style={{width: `${((currentWordIndex + 1) / todaysWords.length) * 100}%`}}></div>
          </div>
          <div className="progress-text">Word {currentWordIndex + 1} of {todaysWords.length}</div>
        </div>
        
        <div className="word-display">
          <div className="word-main">{currentWord.word}</div>
          <div className="word-meaning">{currentWord.meaning}</div>
          <div className="word-example">
            <div className="example-label">Example:</div>
            <div className="example-text">"{currentWord.example}"</div>
          </div>
        </div>

        <div className="learn-actions">
          {currentWordIndex > 0 && (
            <button onClick={() => setCurrentWordIndex(currentWordIndex - 1)} className="nav-btn secondary">
              ← Previous
            </button>
          )}
          
          <button 
            onClick={() => markWordAsLearned(currentWord)} 
            className={`learn-btn ${isWordLearned ? 'learned' : 'primary'}`}
            disabled={isWordLearned}
          >
            {isWordLearned ? '✅ Already Learned' : '✅ I Learned This!'}
          </button>
          
          {currentWordIndex < todaysWords.length - 1 ? (
            <button onClick={() => setCurrentWordIndex(currentWordIndex + 1)} className="nav-btn secondary">
              Next →
            </button>
          ) : (
            <button onClick={() => setCurrentView('home')} className="nav-btn info">
              🏠 Home
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderQuiz = () => {
    if (todaysWords.length === 0) {
      return (
        <div className="quiz-container">
          <button onClick={() => setCurrentView('home')} className="home-btn">🏠 Home</button>
          <h2>🎯 Fun Quiz</h2>
          <p>Please learn today's words first to take the quiz!</p>
        </div>
      );
    }

    if (quizState.questions.length === 0) {
      return (
        <div className="quiz-container">
          <button onClick={() => setCurrentView('home')} className="home-btn">🏠 Home</button>
          <h2>🎯 Fun Quiz</h2>
          <p>Test your knowledge of today's words!</p>
          <button onClick={startQuiz} className="action-btn primary">
            Start Quiz
          </button>
        </div>
      );
    }

    if (quizState.showResult) {
      return (
        <div className="quiz-container">
          <button onClick={() => setCurrentView('home')} className="home-btn">🏠 Home</button>
          <h2>🎯 Quiz Complete!</h2>
          <div className="quiz-result">
            <div className="result-score">
              {quizState.score} / {quizState.questions.length}
            </div>
            <div className="result-message">
              {quizState.score === quizState.questions.length ? 
                "Perfect! You're a word master! 🌟" :
                quizState.score >= quizState.questions.length / 2 ?
                "Great job! Keep learning! 👏" :
                "Good try! Practice makes perfect! 💪"
              }
            </div>
          </div>
          <div className="quiz-actions">
            <button onClick={startQuiz} className="action-btn primary">
              Try Again
            </button>
            <button onClick={() => setCurrentView('home')} className="nav-btn secondary">
              🏠 Home
            </button>
          </div>
        </div>
      );
    }

    const currentQ = quizState.questions[quizState.currentQuestion];
    return (
      <div className="quiz-container">
        <button onClick={() => setCurrentView('home')} className="home-btn">🏠 Home</button>
        <div className="quiz-header">
          <h2>🎯 Fun Quiz</h2>
          <div className="quiz-progress">
            Question {quizState.currentQuestion + 1} of {quizState.questions.length}
          </div>
          <div className="quiz-score">Score: {quizState.score}</div>
        </div>
        
        <div className="quiz-question">
          <div className="question-text">
            Fill in the blank with the correct word:
          </div>
          <div className="sentence">
            {currentQ.sentence.replace(currentQ.correctAnswer.toLowerCase(), '______')}
          </div>
        </div>

        <div className="quiz-options">
          {currentQ.options.map((option, index) => (
            <button
              key={index}
              onClick={() => handleQuizAnswer(option)}
              className={`quiz-option ${quizState.selectedAnswer === option ? 'selected' : ''}`}
            >
              {option}
            </button>
          ))}
        </div>

        <div className="quiz-actions">
          <button 
            onClick={submitQuizAnswer} 
            disabled={!quizState.selectedAnswer}
            className="action-btn primary"
          >
            {quizState.currentQuestion + 1 === quizState.questions.length ? 'Finish Quiz' : 'Next Question'}
          </button>
        </div>
      </div>
    );
  };

  const renderReview = () => {
    const grouped = learnedWords.reduce((acc, word) => {
      const date = new Date(word.learnedDate).toLocaleDateString();
      if (!acc[date]) acc[date] = [];
      acc[date].push(word);
      return acc;
    }, {});

    return (
      <div className="review-container">
        <button onClick={() => setCurrentView('home')} className="home-btn">🏠 Home</button>
        <h2>📚 Review Your Words</h2>
        {learnedWords.length === 0 ? (
          <div className="empty-state">
            <p>You haven't learned any words yet.</p>
            <p>Start with today's words!</p>
          </div>
        ) : (
          <div className="review-groups">
            {Object.entries(grouped).sort((a, b) => new Date(b[0]) - new Date(a[0])).map(([date, words]) => (
              <ReviewDateGroup key={date} date={date} words={words} />
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderProgress = () => (
    <div className="progress-container">
      <button onClick={() => setCurrentView('home')} className="home-btn">🏠 Home</button>
      <h2>📊 My Progress</h2>
      <div className="progress-stats">
        <div className="big-stat">
          <div className="big-number">{totalWordsLearned}</div>
          <div className="big-label">Total Words Learned</div>
        </div>
        <div className="progress-info">
          <div className="info-item">
            📅 Learning since: {learnedWords.length > 0 ? new Date(learnedWords[0].learnedDate).toLocaleDateString() : 'Today'}
          </div>
          <div className="info-item">
            📈 Keep learning every day to grow your vocabulary!
          </div>
        </div>
      </div>
    </div>
  );

  const renderCurrentView = () => {
    switch(currentView) {
      case 'learn': return renderLearn();
      case 'review': return renderReview();
      case 'quiz': return renderQuiz();
      case 'progress': return renderProgress();
      default: return renderHome();
    }
  };

  // Show authentication screen if not logged in
  if (!isAuthenticated) {
    return (
      <div className="App">
        <div className="app-wrapper">
          {renderAuth()}
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <div className="app-wrapper">
        {renderCurrentView()}
      </div>
    </div>
  );
}

export default App;
